import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface DndSchedule {
  enabled: boolean;
  start: string; // "HH:MM" format
  end: string;   // "HH:MM" format
  timezone: string;
}

/**
 * Check if current time is within the DND schedule
 */
function isInDndSchedule(schedule: DndSchedule | null | undefined): boolean {
  if (!schedule || !schedule.enabled || !schedule.start || !schedule.end) {
    return false;
  }

  try {
    const now = new Date();
    const tz = schedule.timezone || 'America/Sao_Paulo';
    
    // Get current time in the user's timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    });
    const parts = formatter.formatToParts(now);
    const currentHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const currentMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    const currentMinutes = currentHour * 60 + currentMinute;

    // Parse schedule times
    const [startHour, startMin] = schedule.start.split(':').map(Number);
    const [endHour, endMin] = schedule.end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    // Handle overnight schedules (e.g., 22:00 - 06:00)
    if (endMinutes < startMinutes) {
      return currentMinutes >= startMinutes || currentMinutes < endMinutes;
    }

    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  } catch (error) {
    console.error('[useDndStatus] Error checking schedule:', error);
    return false;
  }
}

export function useDndStatus() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['dnd-status', user?.id],
    queryFn: async () => {
      if (!user) return { isDnd: false, isManualDnd: false, dndSchedule: null };

      const { data: appUser } = await supabase
        .from('app_users')
        .select('id, is_dnd, dnd_schedule')
        .eq('auth_user_id', user.id)
        .single();

      const isManualDnd = (appUser as any)?.is_dnd ?? false;
      const dndSchedule = (appUser as any)?.dnd_schedule as DndSchedule | null;
      const isScheduleDnd = isInDndSchedule(dndSchedule);

      return {
        isDnd: isManualDnd || isScheduleDnd,
        isManualDnd,
        isScheduleDnd,
        dndSchedule,
      };
    },
    enabled: !!user,
    refetchInterval: 60000, // Check every minute for schedule changes
  });

  const toggleDnd = useMutation({
    mutationFn: async (newStatus: boolean) => {
      if (!user) throw new Error('Not authenticated');

      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!appUser) throw new Error('App user not found');

      const { error } = await supabase
        .from('app_users')
        .update({
          is_dnd: newStatus,
          dnd_updated_at: new Date().toISOString(),
        } as any)
        .eq('id', appUser.id);

      if (error) throw error;

      return newStatus;
    },
    onSuccess: (newStatus) => {
      queryClient.invalidateQueries({ queryKey: ['dnd-status'] });
      toast.success(newStatus ? 'Não Perturbe ativado' : 'Não Perturbe desativado');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar status: ${error.message}`);
    },
  });

  const updateSchedule = useMutation({
    mutationFn: async (schedule: DndSchedule | null) => {
      if (!user) throw new Error('Not authenticated');

      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!appUser) throw new Error('App user not found');

      const { error } = await supabase
        .from('app_users')
        .update({
          dnd_schedule: schedule,
        } as any)
        .eq('id', appUser.id);

      if (error) throw error;

      return schedule;
    },
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: ['dnd-status'] });
      if (schedule?.enabled) {
        toast.success(`Horário de pausa configurado: ${schedule.start} - ${schedule.end}`);
      } else {
        toast.success('Horário de pausa desativado');
      }
    },
    onError: (error: Error) => {
      toast.error(`Erro ao salvar horário: ${error.message}`);
    },
  });

  return {
    isDnd: data?.isDnd ?? false,
    isManualDnd: data?.isManualDnd ?? false,
    isScheduleDnd: data?.isScheduleDnd ?? false,
    dndSchedule: data?.dndSchedule ?? null,
    isLoading,
    toggleDnd: toggleDnd.mutate,
    isToggling: toggleDnd.isPending,
    updateSchedule: updateSchedule.mutate,
    isUpdatingSchedule: updateSchedule.isPending,
  };
}
